import { faPlus } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import {
  Box, Button, DashboardModule, Form, Grid, SectionDivider,
} from '@domain-group/suit-theme'

import { sortArrayBy } from '@domain-group/javascript-utils'

import useProviderPriceListItemOption, {
  useProviderPriceListItemOptionForm,
} from '@hooks/useProviderPriceListItemOption'
import useProviderPriceListItemOptionValues from '@hooks/useProviderPriceListItemOptionValues'

import DeleteIcon from '@components/PriceListManager/components/DeleteIcon'

import { confirmDelete } from '@functions/entity'

import type { ProviderPriceListItemOptionModel } from '@models/types'

import PriceListItemOptionValueSortableList from './PriceListItemOptionValueSortableList'

interface PriceListItemOptionProps {
  callbacks: {
    showCreateOrEditProviderPriceListItemOptionValueModal: (payload: object) => void,
  },
  isMaster: boolean,
  providerPriceListItemOption: ProviderPriceListItemOptionModel,
}

const PriceListItemOption = (props: PriceListItemOptionProps) => {
  const {
    callbacks: {
      showCreateOrEditProviderPriceListItemOptionValueModal,
    },
    isMaster,
    providerPriceListItemOption,
  } = props

  const priceListItemOptionPayload = useProviderPriceListItemOption(providerPriceListItemOption)
  const {
    callbacks: {
      deleteProviderPriceListItemOption: deleteFn,
      toggleVisibility: toggleFn,
      updateProviderPriceListItemOption: updateFn,
    },
  } = priceListItemOptionPayload

  const priceListItemOptionFormPayload = useProviderPriceListItemOptionForm(providerPriceListItemOption)
  const {
    entityState,
    entityState: {
      hidden,
      id,
      product_code,
      title,
    },
    handlers,
  } = priceListItemOptionFormPayload

  const priceListItemOptionValuesPayload = useProviderPriceListItemOptionValues({
    filters: {
      provider_price_list_item_option_id: id,
    },
    performHttpRequests: false,
    sortBy: 'sort asc',
  })

  const {
    filteredProviderPriceListItemOptionValues,
    hasFilteredProviderPriceListItemOptionValues,
  } = priceListItemOptionValuesPayload

  const sortedFilteredProviderPriceListItemOptionValues = sortArrayBy(
    filteredProviderPriceListItemOptionValues,
    'asc',
    'price',
  )

  const confirmDeletePayload = {
    callbacks: {
      deleteFn,
    },
    strings: {
      confirmString: 'Are you sure?',
      entityName: 'Item Option',
    },
  }

  return (
    <DashboardModule
      contentBoxProps={{ flexDirection: 'column' }}
      title={title}
    >
      <Box flexDirection="column" padding="large">
        {isMaster && (
          <Grid
            // @ts-expect-error TS2322
            alignItems="flex-end"
            gridColumnGap="medium"
            gridTemplateColumns="calc(70% - 58px) 30% 92px"
          >
            <Form.Field label="* Option Title">
              <input
                disabled={!isMaster}
                name="title"
                onBlur={() => updateFn(entityState)}
                onChange={handlers.onChange}
                type="text"
                value={title}
              />
            </Form.Field>

            <Form.Field label="Code">
              <input
                disabled={!isMaster}
                name="product_code"
                onBlur={() => updateFn(entityState)}
                onChange={handlers.onChange}
                type="text"
                value={product_code}
              />
            </Form.Field>

            <Box width="auto">
              <Button
                buttonStyle="secondaryDestroy"
                icon={<DeleteIcon hidden={hidden} isMaster={isMaster} />}
                onClick={() => confirmDelete(confirmDeletePayload)}
                size="large"
                style={{ height: 37 }}
                width={42}
              />
            </Box>
          </Grid>
        )}

        {hasFilteredProviderPriceListItemOptionValues && !hidden && (
          <>
            <SectionDivider boxProps={{ marginTop: isMaster ? 'large' : undefined }}>
              Values
            </SectionDivider>

            <PriceListItemOptionValueSortableList
              isMaster={isMaster}
              providerPriceListItemOptionValues={sortedFilteredProviderPriceListItemOptionValues}
            />
          </>
        )}

        {hidden && (
          <DashboardModule.BlankState>
            <DashboardModule.BlankState.Title>
              Option is Hidden
            </DashboardModule.BlankState.Title>

            <DashboardModule.BlankState.Paragraph>
              This option won&apos;t be visible when ordering or quoting. Toggle it using the Show Option button below.
            </DashboardModule.BlankState.Paragraph>
          </DashboardModule.BlankState>
        )}
      </Box>

      <DashboardModule.Footer>
        <DashboardModule.FooterRight>
          {!isMaster && (
            <Button
              buttonStyle={hidden ? 'secondaryCreate' : 'secondaryDestroy'}
              icon={<DeleteIcon hidden={hidden} isMaster={isMaster} />}
              onClick={() => toggleFn()}
              size="small"
              width="auto"
            >
              {hidden ? 'Enable' : 'Disable'} Option
            </Button>
          )}

          {isMaster && (
            <Button
              buttonStyle="secondaryEdit"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => showCreateOrEditProviderPriceListItemOptionValueModal({
                providerPriceListItemOptionValue: {
                  hidden: false,
                  provider_price_list_item_option_id: providerPriceListItemOption.id,
                },
              })}
              size="small"
              width="auto"
            >
              Add Option Value
            </Button>
          )}
        </DashboardModule.FooterRight>
      </DashboardModule.Footer>
    </DashboardModule>
  )
}

export default PriceListItemOption
